import { debounce } from "lodash";
import { AutoCompleteSelectParams } from "primereact/autocomplete";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { IProcedure } from "types/Procedure";
import { IProviderProcedure } from "types/ProviderProcedure";

import {
  buildFilteredProceduresSuggestions,
  buildSelectedProceduresObject,
} from "../../builders";
import {
  autoCompleteFieldHandleOnSelectProcedure,
  autoCompleteHandleOnUnSelectProcedureNew,
} from "../../handlers";
import { CptSuggestion } from "./CptSuggestion";
import { StyledAutoComplete, SUGGESTION_ITEM_HEIGHT } from "./styled";

const DEBOUNCE_DELAY = 500; // ms

interface IAutoComplete {
  search: (e: any) => void;
  autoCompleteValue: string[];
  procedures: IProcedure[];
  providerProcedures: IProviderProcedure[];
  setCurrentProviderProcedures?: any;
  currentProviderProcedures?: IProviderProcedure[];
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Short note under the CPT label (e.g. validation when visit date is required). */
  cptHelperText?: string;
  /** When true, helper text is shown as a validation error. */
  cptHelperError?: boolean;
  placeholder?: string;
  /** CLE-3074 — new rows start on a provider whose Finix payment setup is complete. */
  isFinixAccount?: boolean;
}
export function AutoCompleteCPT({
  search,
  autoCompleteValue,
  procedures,
  providerProcedures,
  setCurrentProviderProcedures,
  currentProviderProcedures,
  disabled = false,
  onFocus,
  onBlur,
  cptHelperText,
  cptHelperError = false,
  placeholder = "Select CPT Code...",
  isFinixAccount = false,
}: IAutoComplete) {
  const dispatch = useDispatch();
  const { rows, selectedProcedures } = useSelector(
    (state: RootState) => state.serviceDetailsSlice
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((e: any) => {
        search(e);
      }, DEBOUNCE_DELAY),
    [search]
  );

  const onSelectProcedure = (e: AutoCompleteSelectParams) => {
    const arrayOfCptCodes = [...selectedProcedures];
    const filteredCpt = arrayOfCptCodes.filter((p) => p === e.value);
    if (currentProviderProcedures && setCurrentProviderProcedures) {
      const copy = [...currentProviderProcedures];
      const existingIds = new Set(copy.map((pp) => pp.id));
      providerProcedures.forEach((procedure) => {
        if (!existingIds.has(procedure.id)) {
          copy.push(procedure);
        }
      });
      setCurrentProviderProcedures(copy);
    }

    autoCompleteFieldHandleOnSelectProcedure({
      e,
      dispatch,
      selectedProcedures,
      procedures,
      providerProcedures,
      rows,
      newValue: filteredCpt.length + 1,
      isFinixAccount,
    });
  };

  const onUnSelectProcedure = (e: AutoCompleteSelectParams) => {
    autoCompleteHandleOnUnSelectProcedureNew({
      e,
      autoCompleteValue,
      rows,
      dispatch,
      selectedProcedures,
    });
    const obj = buildSelectedProceduresObject([e.value]);
    const filteredProviderProcedures = currentProviderProcedures?.filter(
      (pp) => pp?.procedure?.cptCode !== obj[0].code
    );
    setCurrentProviderProcedures(filteredProviderProcedures);
  };

  const renderSuggestion = useCallback(
    (suggestion: string) => (
      <CptSuggestion
        suggestion={suggestion}
        providerProcedures={providerProcedures}
      />
    ),
    [providerProcedures]
  );

  const handleSuggestion = useMemo(() => {
    const suggestions = buildFilteredProceduresSuggestions(procedures);
    return suggestions;
  }, [procedures]);

  return (
    <>
      <label
        htmlFor="code"
        className="text-sm font-semibold mb-2"
        style={{ color: "var(--brand-black)" }}
      >
        Add CPT services
      </label>
      {cptHelperText ? (
        <p
          className={
            cptHelperError
              ? "p-error text-sm line-height-3 m-0 mb-2"
              : "text-600 text-sm line-height-3 m-0 mb-2"
          }
          id="cpt-search-helper-text"
        >
          {cptHelperText}
        </p>
      ) : null}
      <StyledAutoComplete
        multiple
        panelStyle={{
          padding: 0,
          overflow: "hidden",
          borderRadius: "16px",
          border: "1px solid var(--brand-gray-200)",
          boxShadow:
            "0 4px 6px color-mix(in srgb, var(--brand-black) 5%, transparent), 0 12px 28px color-mix(in srgb, var(--brand-black) 12%, transparent)",
        }}
        itemTemplate={renderSuggestion}
        id="code"
        disabled={disabled}
        className="w-full"
        completeMethod={debouncedSearch}
        value={autoCompleteValue}
        data-testid="autocompleteCPT_ServiceDetails"
        placeholder={placeholder}
        suggestions={handleSuggestion}
        onSelect={onSelectProcedure}
        onUnselect={onUnSelectProcedure}
        onFocus={onFocus}
        onBlur={onBlur}
        virtualScrollerOptions={{ itemSize: SUGGESTION_ITEM_HEIGHT }}
        emptyMessage="No results found"
        minLength={1}
      />
    </>
  );
}
